import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  RefreshCw,
  TrendingUp,
  AlertTriangle,
  Euro,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  Sparkles,
  Phone,
  ChevronRight,
  Loader2,
  BarChart3,
  Users,
  Zap,
  Printer,
} from 'lucide-react';

const PRIORITY_CONFIG = {
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500', border: 'border-l-red-500' },
  haute:   { label: 'Haute',   color: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-500', border: 'border-l-orange-500' },
  normale: { label: 'Normale', color: 'bg-blue-100 text-blue-800 border-blue-200', dot: 'bg-blue-400', border: 'border-l-blue-400' },
};

const STATUS_LABEL = {
  open: 'Ouverte',
  won: 'Gagnée',
  lost: 'Perdue',
  postponed: 'Reportée',
};

const RESULT_COLORS = {
  interesse: 'bg-green-100 text-green-700',
  a_rappeler: 'bg-orange-100 text-orange-700',
  non_joignable: 'bg-gray-100 text-gray-600',
  ne_repond_pas: 'bg-gray-100 text-gray-600',
  hesitant: 'bg-yellow-100 text-yellow-700',
  refuse: 'bg-red-100 text-red-700',
  reporte: 'bg-blue-100 text-blue-700',
};

const RESULT_LABELS = {
  interesse: 'Intéressé',
  a_rappeler: 'À rappeler',
  non_joignable: 'Non joignable',
  ne_repond_pas: 'Ne répond pas',
  hesitant: 'Hésitant',
  refuse: 'Refusé',
  reporte: 'Reporté',
  stage_change: 'Changement étape',
  create: 'Création',
  close: 'Clôture',
  contact: 'Contact',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatPrice(v) {
  if (!v) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Aujourd'hui";
  if (days === 1) return 'Hier';
  if (days < 7) return `Il y a ${days}j`;
  if (days < 30) return `Il y a ${Math.floor(days / 7)}sem`;
  return `Il y a ${Math.floor(days / 30)}mois`;
}

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-slate-800', bg = 'bg-white' }) => (
  <Card className={`${bg} border border-slate-200 shadow-sm`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
          <Icon className="w-5 h-5 text-slate-500" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function CommercialReportPage({ embedded = false }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [period, setPeriod] = useState('30');
  const [filter, setFilter] = useState('all'); // all | open | overdue
  const printRef = useRef(null);

  useEffect(() => {
    fetchReport();
  }, [period]);

  // Chargement rapide : sans IA
  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/opportunities/commercial-report?period_days=${period}&skip_ai=true`);
      setReport(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  // Génération IA séparée (à la demande)
  const generateAI = async () => {
    setAiLoading(true);
    try {
      const res = await api.get(`/opportunities/commercial-report?period_days=${period}&skip_ai=false`);
      setReport(prev => ({
        ...prev,
        ai_recommendations: res.data.ai_recommendations,
        ai_error: res.data.ai_error,
      }));
      if (res.data.ai_recommendations?.length > 0) {
        toast.success(`${res.data.ai_recommendations.length} recommandations générées`);
      }
    } catch (err) {
      toast.error('Erreur lors de la génération IA');
    } finally {
      setAiLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredOpps = () => {
    if (!report?.opportunities) return [];
    const opps = report.opportunities;
    if (filter === 'open') return opps.filter(o => o.status === 'open');
    if (filter === 'overdue') return opps.filter(o => o.is_overdue);
    return opps;
  };

  const stats = report?.stats || {};

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 max-w-7xl mx-auto space-y-6'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rapport Commercial</h1>
          <p className="text-sm text-slate-500 mt-1">
            Vue synthétique du pipeline avec recommandations IA
            {report?.generated_at && (
              <span className="ml-2 text-slate-400">
                · Généré le {formatDate(report.generated_at)} à {new Date(report.generated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={fetchReport}
            disabled={loading}
            data-testid="refresh-report-btn"
            variant="outline"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Actualiser
          </Button>
          {report && (
            <Button
              onClick={handlePrint}
              data-testid="print-report-btn"
              className="bg-slate-900 text-white hover:bg-slate-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
          )}        </div>
      </div>

      {loading && !report && (
        <div className="flex flex-col items-center justify-center py-24 space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-slate-400" />
          <p className="text-slate-500">Chargement du rapport…</p>
        </div>
      )}

      {report && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-section">
            <StatCard
              icon={Target}
              label="Pipeline ouvert"
              value={stats.total_open ?? '—'}
              sub={`${stats.overdue_count ?? 0} en retard`}
              color={stats.overdue_count > 0 ? 'text-orange-600' : 'text-slate-900'}
            />
            <StatCard
              icon={Euro}
              label="CA potentiel"
              value={formatPrice(stats.total_pipeline_amount)}
              sub="Opportunités ouvertes"
            />
            <StatCard
              icon={TrendingUp}
              label="Gagnées"
              value={stats.won_this_period ?? 0}
              sub={`sur ${period}j · ${stats.total_won ?? 0} total`}
              color="text-green-600"
            />
            <StatCard
              icon={BarChart3}
              label="Taux de conversion"
              value={`${stats.conversion_rate ?? 0}%`}
              sub={`${stats.lost_this_period ?? 0} perdues sur ${period}j`}
              color={stats.conversion_rate >= 30 ? 'text-green-600' : 'text-orange-600'}
            />
          </div>

          {/* Répartition pipeline */}
          {stats.stage_counts && Object.keys(stats.stage_counts).length > 0 && (
            <Card className="border border-slate-200 shadow-sm" data-testid="pipeline-breakdown">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-slate-500" />
                  Répartition du Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.stage_counts).map(([stage, count]) => (
                    <div
                      key={stage}
                      className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <span className="text-lg font-bold text-slate-800">{count}</span>
                      <span className="text-xs text-slate-500 capitalize">{stage.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Recommendations */}
          <Card className="border border-slate-200 shadow-sm" data-testid="ai-recommendations">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  Recommandations IA
                  {report.ai_recommendations?.length > 0 && (
                    <span className="text-xs font-normal text-slate-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Analyse Claude IA
                    </span>
                  )}
                </CardTitle>
                {!report.ai_recommendations?.length && !aiLoading && (
                  <Button
                    size="sm"
                    onClick={generateAI}
                    data-testid="generate-ai-btn"
                    className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-8"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    Analyser avec l'IA
                  </Button>
                )}
                {aiLoading && (
                  <span className="flex items-center gap-2 text-xs text-violet-600">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Analyse en cours…
                  </span>
                )}
                {report.ai_recommendations?.length > 0 && !aiLoading && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={generateAI}
                    data-testid="regenerate-ai-btn"
                    className="text-xs text-slate-400 h-7"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Regénérer
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* État initial : inviter à générer */}
              {!report.ai_recommendations?.length && !aiLoading && !report.ai_error && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center mb-3">
                    <Sparkles className="w-6 h-6 text-violet-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">Obtenez des recommandations personnalisées</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-sm">
                    L'IA analysera vos {stats.total_open} opportunités et les {stats.overdue_count} retards pour générer 5 actions prioritaires.
                  </p>
                  <Button
                    onClick={generateAI}
                    data-testid="generate-ai-cta-btn"
                    className="mt-4 bg-violet-600 hover:bg-violet-700 text-white text-sm"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser avec l'IA (~10s)
                  </Button>
                </div>
              )}

              {/* Chargement IA */}
              {aiLoading && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-violet-400 mb-3" />
                  <p className="text-sm font-medium text-slate-700">Claude IA analyse votre pipeline…</p>
                  <p className="text-xs text-slate-400 mt-1">Cela prend généralement 10 à 15 secondes</p>
                </div>
              )}

              {/* Recommandations affichées */}
              {!aiLoading && report.ai_recommendations?.length > 0 && (
                <div className="space-y-3">
                  {report.ai_recommendations.map((rec, i) => {
                    const p = PRIORITY_CONFIG[rec.priorite] || PRIORITY_CONFIG.normale;
                    return (
                      <div
                        key={i}
                        className={`border-l-4 ${p.border} bg-white border border-slate-100 rounded-r-lg p-4 shadow-sm`}
                        data-testid={`recommendation-${i}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${p.color}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
                                {p.label}
                              </span>
                              <span className="text-sm font-semibold text-slate-800">{rec.action}</span>
                            </div>
                            <p className="text-sm text-slate-600">{rec.detail}</p>
                            {rec.impact && (
                              <p className="text-xs text-slate-400 mt-1 italic">{rec.impact}</p>
                            )}
                            {rec.clients_concernes?.length > 0 && (
                              <div className="flex gap-1 mt-2 flex-wrap">
                                {rec.clients_concernes.map((c, j) => (
                                  <span key={j} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200">
                                    {c}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className="text-2xl font-black text-slate-100 select-none">{i + 1}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Erreur IA */}
              {!aiLoading && report.ai_error && (
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Recommandations IA non disponibles</p>
                    <p className="text-xs text-amber-600">{report.ai_error}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Table des opportunités */}
          <Card className="border border-slate-200 shadow-sm" data-testid="opportunities-table">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-500" />
                  Détail des Opportunités
                  <span className="text-sm font-normal text-slate-400">({filteredOpps().length})</span>
                </CardTitle>
                <div className="flex gap-2">
                  {['all', 'open', 'overdue'].map(f => (
                    <Button
                      key={f}
                      size="sm"
                      variant={filter === f ? 'default' : 'outline'}
                      className={`text-xs h-7 ${filter === f ? 'bg-slate-900 text-white' : ''}`}
                      onClick={() => setFilter(f)}
                      data-testid={`filter-${f}`}
                    >
                      {f === 'all' ? 'Toutes' : f === 'open' ? 'Ouvertes' : 'En retard'}
                      {f === 'overdue' && stats.overdue_count > 0 && (
                        <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">{stats.overdue_count}</span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium text-slate-600 text-xs uppercase tracking-wide">Client</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600 text-xs uppercase tracking-wide">Étape</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600 text-xs uppercase tracking-wide">Montant</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600 text-xs uppercase tracking-wide">Dernière activité</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600 text-xs uppercase tracking-wide">Tâches</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600 text-xs uppercase tracking-wide">Statut</th>
                      <th className="py-3 px-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredOpps().length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-10 text-slate-400">Aucune opportunité</td>
                      </tr>
                    )}
                    {filteredOpps().map(opp => (
                      <tr
                        key={opp.id}
                        className={`hover:bg-slate-50 transition-colors ${opp.is_overdue ? 'bg-red-50/40' : ''}`}
                        data-testid={`opp-row-${opp.id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="font-medium text-slate-800">{opp.client_name}</div>
                          {opp.client_phone && (
                            <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
                              <Phone className="w-3 h-3" />
                              {opp.client_phone}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full text-white"
                            style={{ backgroundColor: opp.stage_color || '#6B7280' }}
                          >
                            {opp.stage_label}
                          </span>
                          {opp.is_overdue && (
                            <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                              <Clock className="w-3 h-3" />
                              {opp.overdue_hours < 24 ? `${opp.overdue_hours}h` : `${Math.floor(opp.overdue_hours / 24)}j`} de retard
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium text-slate-700">{formatPrice(opp.estimated_amount)}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-slate-600">{timeAgo(opp.last_activity_date)}</div>
                          {opp.last_activity_result && (
                            <span className={`inline-block text-xs px-1.5 py-0.5 rounded mt-0.5 ${RESULT_COLORS[opp.last_activity_result] || 'bg-slate-100 text-slate-600'}`}>
                              {RESULT_LABELS[opp.last_activity_result] || opp.last_activity_result}
                            </span>
                          )}
                          {opp.last_activity_notes && (
                            <div className="text-xs text-slate-400 mt-0.5 max-w-xs truncate" title={opp.last_activity_notes}>
                              {opp.last_activity_notes}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {opp.pending_tasks_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                              <Clock className="w-3 h-3" />
                              {opp.pending_tasks_count} tâche{opp.pending_tasks_count > 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {opp.status === 'open' ? (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Ouvert</span>
                          ) : opp.status === 'won' ? (
                            <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full w-fit">
                              <CheckCircle className="w-3 h-3" />Gagné
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full w-fit">
                              <XCircle className="w-3 h-3" />Perdu
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {opp.id && (
                            <Link
                              to={`/admin/opportunities/${opp.id}`}
                              className="text-slate-400 hover:text-slate-700 transition-colors"
                              data-testid={`opp-link-${opp.id}`}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ============ ZONE D'IMPRESSION ============ */}
      {report && (
        <div className="print-only" data-testid="print-zone">
          <style>{`
            @media print {
              body * { visibility: hidden !important; }
              .print-only, .print-only * { visibility: visible !important; }
              .print-only {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                width: 100% !important;
                background: white !important;
                padding: 16px 24px !important;
                font-family: Arial, sans-serif !important;
                font-size: 11px !important;
                color: #1e293b !important;
              }
              .print-table { width: 100%; border-collapse: collapse; margin-top: 12px; }
              .print-table th {
                background: #1e293b !important;
                color: white !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                padding: 7px 10px;
                text-align: left;
                font-size: 10px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                border: 1px solid #334155;
              }
              .print-table td {
                padding: 7px 10px;
                border: 1px solid #e2e8f0;
                vertical-align: top;
                font-size: 10px;
                line-height: 1.4;
              }
              .print-table tr:nth-child(even) td { background: #f8fafc !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .print-table tr.overdue td { background: #fff1f2 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .stage-badge {
                display: inline-block;
                padding: 2px 7px;
                border-radius: 9999px;
                font-size: 9px;
                font-weight: 600;
                color: white !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .print-meta { color: #64748b; font-size: 9px; margin-top: 2px; }
              .print-action { font-weight: 600; color: #0f172a; }
              .print-note { color: #475569; font-style: italic; margin-top: 2px; }
              .print-header { border-bottom: 2px solid #1e293b; padding-bottom: 8px; margin-bottom: 4px; }
              .print-header h1 { font-size: 18px; font-weight: 700; margin: 0; }
              .print-header p { font-size: 10px; color: #64748b; margin: 2px 0 0 0; }
              .print-stats { display: flex; gap: 24px; margin: 10px 0; }
              .print-stat { text-align: center; }
              .print-stat-val { font-size: 20px; font-weight: 700; }
              .print-stat-lbl { font-size: 9px; color: #64748b; text-transform: uppercase; }
              .print-footer { margin-top: 12px; font-size: 9px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 8px; }
              .no-print { display: none !important; }
              @page { size: A4 landscape; margin: 12mm 10mm; }
            }
            @media screen { .print-only { display: none; } }
          `}</style>

          {/* En-tête */}
          <div className="print-header">
            <h1>Rapport Commercial — IDMAT OI</h1>
            <p>
              Généré le {formatDate(report.generated_at)} à {new Date(report.generated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              &nbsp;·&nbsp; Période : {report.period_days} jours
            </p>
          </div>

          {/* Stats */}
          <div className="print-stats">
            <div className="print-stat">
              <div className="print-stat-val">{stats.total_open ?? 0}</div>
              <div className="print-stat-lbl">Pipeline ouvert</div>
            </div>
            <div className="print-stat">
              <div className="print-stat-val" style={{ color: '#dc2626' }}>{stats.overdue_count ?? 0}</div>
              <div className="print-stat-lbl">En retard</div>
            </div>
            <div className="print-stat">
              <div className="print-stat-val">{stats.total_pipeline_amount ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(stats.total_pipeline_amount) + ' €' : '—'}</div>
              <div className="print-stat-lbl">CA potentiel</div>
            </div>
            <div className="print-stat">
              <div className="print-stat-val">{stats.won_this_period ?? 0}</div>
              <div className="print-stat-lbl">Gagnées ({report.period_days}j)</div>
            </div>
            <div className="print-stat">
              <div className="print-stat-val">{stats.conversion_rate ?? 0}%</div>
              <div className="print-stat-lbl">Taux de conversion</div>
            </div>
          </div>

          {/* Tableau principal */}
          <table className="print-table">
            <thead>
              <tr>
                <th style={{ width: '14%' }}>Client</th>
                <th style={{ width: '10%' }}>Étape</th>
                <th style={{ width: '12%' }}>Montant / Devis</th>
                <th style={{ width: '27%' }}>Dernières notes de suivi</th>
                <th style={{ width: '22%' }}>Notes devis</th>
                <th style={{ width: '15%' }}>Action à mener</th>
              </tr>
            </thead>
            <tbody>
              {(report.opportunities || []).filter(o => o.status === 'open').map((opp, i) => {
                const combinedNotes = [
                  opp.last_activity_notes,
                  opp.devis_last_activity_note,
                ].filter(Boolean).join(' | ') || '—';

                const devisNotes = opp.devis_note || '—';

                return (
                  <tr key={opp.id || i} className={opp.is_overdue ? 'overdue' : ''}>
                    <td>
                      <strong>{opp.client_name}</strong>
                      {opp.client_phone && (
                        <div className="print-meta">{opp.client_phone}</div>
                      )}
                      {opp.is_overdue && (
                        <div className="print-meta" style={{ color: '#dc2626' }}>
                          ⚠ {opp.overdue_hours < 24 ? `${opp.overdue_hours}h` : `${Math.floor(opp.overdue_hours / 24)}j`} de retard
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className="stage-badge"
                        style={{ backgroundColor: opp.stage_color || '#6B7280' }}
                      >
                        {opp.stage_label}
                      </span>
                    </td>
                    <td>
                      {opp.estimated_amount
                        ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(opp.estimated_amount) + ' €'
                        : '—'}
                      {opp.devis_reference && (
                        <div className="print-meta">{opp.devis_reference}</div>
                      )}
                    </td>
                    <td>
                      <div>{combinedNotes}</div>
                      {opp.last_activity_date && (
                        <div className="print-meta">Dernier contact : {formatDate(opp.last_activity_date)}</div>
                      )}
                    </td>
                    <td>
                      <div className="print-note">{devisNotes}</div>
                    </td>
                    <td>
                      <div className="print-action">{opp.suggested_action || '—'}</div>
                      {opp.pending_tasks_count > 0 && (
                        <div className="print-meta">{opp.pending_tasks_count} tâche(s) en attente</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="print-footer">
            IDMAT OI — CRM Commercial · Rapport généré le {formatDate(report.generated_at)} · Confidentiel
          </div>
        </div>
      )}
    </div>
  );
}
